// frontend/src/components/product/ProductDetails.js - Complete MeatBuddy Layout Match

import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProduct } from '../../actions/productActions';
import { addCartItem } from '../../actions/cartActions';
import { toast } from 'react-toastify';
import {
    ShoppingCart,
    Minus,
    Plus,
    AlertCircle,
    Truck,
    Shield,
    Loader2,
    ArrowLeft,
    Check,
    ChevronDown,
    ChevronUp,
    ArrowRight,
    IndianRupee,
    CircleMinus,
    CirclePlus,
    Scale
} from 'lucide-react';
import MetaData from '../layouts/MetaData';
import Loader from '../layouts/Loader';
import RelatedProducts from './RelatedProducts';

export default function ProductDetails() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    
    const { loading, product } = useSelector(state => state.productState);
    
    const [quantity, setQuantity] = useState(1);
    const [selectedWeight, setSelectedWeight] = useState('');
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [addingToCart, setAddingToCart] = useState(false);
    const [isZoomed, setIsZoomed] = useState(false);
    
    // State for collapsible sections
    const [openSections, setOpenSections] = useState({
        description: true,
        highlights: false,
        storage: false
    });

    useEffect(() => {
        // Force refresh to get latest data
        dispatch(getProduct(id, true));
    }, [dispatch, id]);

    useEffect(() => {
        if (product && product.priceType === 'fixed' && product.weightOptions) {
            const visibleOptions = product.weightOptions.filter(opt => opt.visible);
            if (visibleOptions.length > 0) {
                setSelectedWeight(visibleOptions[0].weight);
            }
        }
    }, [product]);

    const handleBackToShop = useCallback((e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        navigate('/shop');
    }, [navigate]);

    const increaseQty = () => {
        if (quantity < 10) {
            setQuantity(quantity + 1);
        }
    };

    const decreaseQty = () => {
        if (quantity > 1) {
            setQuantity(quantity - 1);
        }
    };

    const addToCart = async () => {
        try {
            setAddingToCart(true);
            
            if (product.priceType === 'fixed' && !selectedWeight) {
                toast.error('Please select a weight option');
                return;
            }
            
            await dispatch(addCartItem(product._id, quantity, selectedWeight));
            toast.success('Item added to cart successfully!');
        } catch (error) {
            toast.error('Failed to add item to cart');
        } finally {
            setAddingToCart(false);
        }
    };

    const getCurrentPrice = () => {
        if (!product) return 0;

        if (product.priceType === 'perKg') {
            return product.price;
        } else if (product.priceType === 'perDozen') {
            return product.dozenPrice;
        } else if (product.priceType === 'perPack') {
            return product.packPrice;
        } else if (product.priceType === 'fixed' && selectedWeight) {
            const option = product.weightOptions.find(opt => opt.weight === selectedWeight);
            if (option) {
                return option.price;
            }
        }
        return 0;
    };

    const toggleSection = (section) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleImageHover = () => {
        setIsZoomed(true);
    };

    const handleImageLeave = () => {
        setIsZoomed(false);
    };

    if (loading) return <Loader />;

    if (!product) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-lg font-medium text-gray-900">Product not found</h3>
                    <p className="mt-1 text-sm text-gray-500">The product you're looking for doesn't exist.</p>
                </div>
            </div>
        );
    }

    const visibleWeightOptions = product.weightOptions?.filter(opt => opt.visible) || [];

    return (
        <Fragment>
            <MetaData title={product.name} />
            
            {/* Main Container with Background */}
            <div
                className="min-h-screen"
                style={{
                    backgroundImage: `url('https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-background-image.png')`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    backgroundPosition: 'center'
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Back to Shop Button - Top Right */}
                    <div className="flex justify-end items-center mb-6">
                        <Link
                            to="/shop"
                            className="flex items-center gap-2 text-gray-600 hover:text-[#dc2626] transition-colors no-underline"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            <span className="text-xs">Back to Shop</span>
                        </Link>
                    </div>

                    {/* Main Product Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Left Column - Product Images */}
                        <div className="space-y-4">
                            {/* Main Product Image */}
                            <div className="relative">
                                <div className="aspect-w-16 aspect-h-16 rounded-lg overflow-hidden">
                                    <div 
                                        className="relative w-full h-[320px]"
                                        onMouseEnter={handleImageHover}
                                        onMouseLeave={handleImageLeave}
                                    >
                                        <img
                                            src={product.images[selectedImageIndex]?.image}
                                            alt={product.name}
                                            className={`w-full h-full object-contain transition-transform duration-200 ${
                                                isZoomed ? 'scale-110' : 'scale-100'
                                            }`}
                                            style={{
                                                transformOrigin: 'center center',
                                                cursor: 'zoom-in'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            {/* Thumbnail Images */}
                            {product.images && product.images.length > 1 && (
                                <div className="flex gap-2 overflow-x-auto py-2">
                                    {product.images.map((image, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setSelectedImageIndex(index)}
                                            className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${
                                                selectedImageIndex === index ? 'border-[#dc2626]' : 'border-gray-200'
                                            }`}
                                        >
                                            <img
                                                src={image.image}
                                                alt={`${product.name} thumbnail ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Right Column - Product Details */}
                        <div className="space-y-6">
                            {/* Product Title */}
                            <div>
                                <h1 className="text-lg font-bold text-gray-900 font-['Crimson_Text']">
                                    {product.name}
                                </h1>
                            </div>

                            {/* Price Section */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl font-bold text-[#dc2626] flex items-center">
                                        <IndianRupee className="w-5 h-5 mr-1" />
                                        {getCurrentPrice().toFixed(2)}
                                    </span>
                                    {product.priceType === 'perKg' && (
                                        <span className="text-sm text-gray-600">/kg</span>
                                    )}
                                    {product.priceType === 'perDozen' && (
                                        <span className="text-sm text-gray-600">/dozen</span>
                                    )}
                                    {product.priceType === 'perPack' && (
                                        <span className="text-sm text-gray-600">/pack</span>
                                    )}
                                </div>
                            </div>

                            {/* Weight Options for Fixed Price Products */}
                            {product.priceType === 'fixed' && visibleWeightOptions.length > 0 && (
                                <div className="space-y-2">
                                    <label className="block text-xs font-medium text-gray-700">Select Weight</label>
                                    <div className="flex gap-3">
                                        {visibleWeightOptions.map((option) => (
                                            <button
                                                key={option.weight}
                                                onClick={() => setSelectedWeight(option.weight)}
                                                className={`px-3 py-1.5 rounded-lg border-2 transition-colors text-xs ${
                                                    selectedWeight === option.weight
                                                        ? 'border-[#dc2626] text-[#dc2626] bg-red-50'
                                                        : 'border-gray-300 text-gray-700 hover:border-[#dc2626]'
                                                }`}
                                            >
                                                {option.weight}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Quantity Selector */}
                            <div className="space-y-2">
                                <label className="block text-xs font-medium text-gray-700">Quantity</label>
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={decreaseQty}
                                        disabled={quantity <= 1}
                                        className="text-[#dc2626] hover:bg-red-50 p-1 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <CircleMinus className="w-5 h-5" />
                                    </button>
                                    <input
                                        type="text"
                                        className="w-16 text-center border border-gray-300 rounded-lg px-2 py-1 text-xs"
                                        min="1"
                                        max="10"
                                        value={quantity}
                                        readOnly
                                    />
                                    <button
                                        onClick={increaseQty}
                                        disabled={quantity >= 10}
                                        className="text-[#dc2626] hover:bg-red-50 p-1 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <CirclePlus className="w-5 h-5" />
                                    </button>
                                    {product.priceType === 'perKg' && (
                                        <span className="text-xs text-gray-600">kg</span>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons - Mobile & Desktop */}
                            <div className="flex items-center gap-4 mb-4">
                                {/* Mobile View - Stacked Buttons */}
                                <div className="md:hidden w-full space-y-2">
                                    <button
                                        onClick={addToCart}
                                        disabled={addingToCart || (product.priceType === 'fixed' && !selectedWeight)}
                                        className="w-full bg-[#dc2626] text-white py-2 rounded-lg text-xs font-medium hover:bg-[#dc2626]/90 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ShoppingCart className="w-4 h-4 mr-2" />
                                        Add to Cart
                                    </button>
                                    <Link
                                        to="/cart"
                                        className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors flex items-center justify-center no-underline"
                                    >
                                        <ArrowRight className="w-4 h-4 mr-2" />
                                        Go to Cart
                                    </Link>
                                </div>
                                
                                {/* Desktop View - Side by Side */}
                                <div className="hidden md:flex gap-4 w-full">
                                    <button
                                        onClick={addToCart}
                                        disabled={addingToCart || (product.priceType === 'fixed' && !selectedWeight)}
                                        className="w-1/3 bg-[#dc2626] text-white py-2 rounded-lg text-xs font-medium hover:bg-[#dc2626]/90 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ShoppingCart className="w-4 h-4 mr-2" />
                                        Add to Cart
                                    </button>
                                    <Link
                                        to="/cart"
                                        className="w-1/3 bg-gray-100 text-gray-700 py-2 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors flex items-center justify-center no-underline"
                                    >
                                        <ArrowRight className="w-4 h-4 mr-2" />
                                        Go to Cart
                                    </Link>
                                </div>
                            </div>

                            {/* Product Features */}
                            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                                <div className="flex items-start gap-2">
                                    <Truck className="w-4 h-4 text-[#dc2626]" />
                                    <div>
                                        <p className="font-medium text-xs text-gray-900">Fast Delivery</p>
                                        <p className="text-xs text-gray-500">Same day delivery available</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2">
                                    <Scale className="w-4 h-4 text-[#dc2626]" />
                                    <div>
                                        <p className="font-medium text-xs text-gray-900">Fresh Quality</p>
                                        <p className="text-xs text-gray-500">100% fresh meat guaranteed</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Related Products Section */}
                    <div className="mt-8">
                        <RelatedProducts currentProduct={product} />
                    </div>

                    {/* Collapsible Sections */}
                    <div className="mt-8 max-w-4xl mx-auto">
                        {/* Product Description */}
                        <div className="border-b border-gray-200">
                            <button
                                onClick={() => toggleSection('description')}
                                className="w-full py-3 px-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
                            >
                                <span className="text-sm font-semibold text-gray-900">Product Description</span>
                                {openSections.description ? (
                                    <ChevronUp className="w-4 h-4" />
                                ) : (
                                    <ChevronDown className="w-4 h-4" />
                                )}
                            </button>
                            {openSections.description && (
                                <div className="p-4">
                                    <p className="text-sm text-gray-600 leading-relaxed">
                                        {product.description}
                                    </p>
                                </div>
                            )}
                        </div>
                       
                        {/* Key Highlights */}
                        {product.keyHighlights && (
                            <div className="border-b border-gray-200">
                                <button
                                    onClick={() => toggleSection('highlights')}
                                    className="w-full py-3 px-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
                                >
                                    <span className="text-sm font-semibold text-gray-900">Key Highlights</span>
                                    {openSections.highlights ? (
                                        <ChevronUp className="w-4 h-4" />
                                    ) : (
                                        <ChevronDown className="w-4 h-4" />
                                    )}
                                </button>
                                {openSections.highlights && (
                                    <div className="p-4">
                                        <div className="space-y-2">
                                            {product.keyHighlights.split('\n').map((highlight, index) => (
                                                <div key={index} className="flex items-start space-x-3">
                                                    <Check className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                                                    <span className="text-gray-700 text-sm">{highlight}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                       
                        {/* Storage Instructions */}
                        {product.storageInstructions && (
                            <div className="border-b border-gray-200">
                                <button
                                    onClick={() => toggleSection('storage')}
                                    className="w-full py-3 px-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
                                >
                                    <span className="text-sm font-semibold text-gray-900">Storage Instructions</span>
                                    {openSections.storage ? (
                                        <ChevronUp className="w-4 h-4" />
                                    ) : (
                                        <ChevronDown className="w-4 h-4" />
                                    )}
                                </button>
                                {openSections.storage && (
                                    <div className="p-4">
                                        <p className="text-sm text-gray-600 leading-relaxed">
                                            {product.storageInstructions}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Fragment>
    );
}